import React, { useEffect, useState } from "react";
import ArtworkDetailTab from "./tabs/ArtworkDetailTab";

// Exemple de données pour démonstration

const sampleArtwork = {
  title: "Monochrome(s)",
  artist: "Reynald Drouhin",
  year: "2018",
  typeOfArt: "Art Contemporain",
  medium: "Art numérique",
  description: "tirage lambda sur diasec avec châssis aluminium. Monochrome(s) de Reynald Drouhin est une exploration sur la recherche d'images sur le Web suivant l'approche des photomosaïques, en vogue depuis plusieurs années. Cependant, au lieu de représenter une image concrète, Drouhin a plutôt choisi de s'approcher le plus possible d'une couleur en aplat (il y a cinq couleurs disponibles: rouge, vert, bleu, noir et blanc). Pour ce faire, plutôt que d'avoir présélectionné des images, il a choisi d'utiliser les résultats de recherche qu'offrent le Web pour créer ses mosaïques. En effet, elles sont constituées d'images trouvées sur Google Images, et sont constamment mise à jour. Ce ne sont donc pas des images statiques, mais bien des mosaïques génératives en mouvement.",
  signature: {
    isSigned: true,
    location: "Au dos",
    type: "Signature manuscrite"
  },
  edition: {
    isLimited: true,
    number: 3,
    total: 5
  },
  acquisitionDetails: {
    type: "Achat",
    date: "2023-05-15",
    source: "Galerie XYZ",
    price: 3500
  },
  tags: ["photomosaïque", "art numérique", "web"]
};

const additionalExamples = [
  {
    name: "Estampe ukiyo-e",
    description: "Estampe japonaise ukiyo-e de l'époque Edo, représentant une scène de paysage avec le Mont Fuji et des voyageurs sur un pont. Gravée sur bois par l'artiste Hokusai vers 1831, cette œuvre fait partie de la célèbre série 'Trente-six vues du mont Fuji'. Les couleurs dominantes sont le bleu, le vert et l'ocre, avec une utilisation caractéristique du bleu de Prusse. L'œuvre illustre parfaitement le style ukiyo-e avec ses perspectives planes et ses contours nets."
  },
  {
    name: "Installation vidéo contemporaine",
    description: "Installation vidéo immersive composée de trois écrans LED synchronisés, projetant des séquences filmées dans des contextes urbains. L'artiste explore les thèmes de la surveillance et de l'anonymat dans les espaces publics modernes. La bande sonore comprend des bruits urbains mixés avec une composition électronique minimaliste. L'œuvre est conçue pour une expérience sensorielle complète, où le spectateur est invité à circuler entre les écrans, devenant ainsi partie intégrante de l'installation."
  },
  {
    name: "Œuvre de bioart",
    description: "Œuvre de bioart créée à partir de cultures de bactéries vivantes placées dans des boîtes de Petri. L'artiste a manipulé génétiquement ces organismes pour qu'ils produisent des pigments colorés formant des motifs évolutifs. L'installation comprend un système de maintien en vie pour les cultures et un dispositif de captation qui projette en temps réel leur évolution sur un mur adjacent. Cette œuvre interroge notre rapport à la manipulation du vivant et aux frontières entre art, technologie et science."
  }
];

export default function ApiTagDemoApp() {
  const [formData, setFormData] = useState(sampleArtwork);
  const [activeTab, setActiveTab] = useState("details");
  const [selectedExample, setSelectedExample] = useState(0);

  // L'analyse IA passe par le backend (aucune clé OpenAI côté navigateur).
  useEffect(() => {
    if (!localStorage.getItem('artworkApiSettings')) {
      localStorage.setItem(
        'artworkApiSettings',
        JSON.stringify({ endpoint: 'openai' })
      );
    }
  }, []);

  // Charger un exemple de description
  const loadExample = (index) => {
    if (index === 0) {
      setFormData(prevState => ({
        ...prevState,
        description: sampleArtwork.description,
        tags: [] // Réinitialiser les tags
      }));
    } else {
      setFormData(prevState => ({
        ...prevState,
        description: additionalExamples[index - 1].description,
        tags: [] // Réinitialiser les tags
      }));
    }
    setSelectedExample(index);
  };

  return (
    <div className="max-w-5xl mx-auto p-6 bg-white">
      <h1 className="text-3xl font-bold mb-2">Analyse de tags avec IA</h1>
      <p className="mb-6 text-gray-600">
        Ce démonstrateur vous permet de comparer différentes méthodes de génération de tags pour vos œuvres d'art.
      </p>
      
      {/* Sélecteur d'exemples */}
      <div className="mb-8 bg-gray-50 p-4 rounded-md border border-gray-200">
        <h2 className="text-lg font-medium mb-3">Exemples de descriptions</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <button
            onClick={() => loadExample(0)}
            className={`p-3 text-left rounded ${
              selectedExample === 0 
                ? 'bg-blue-100 border border-blue-300' 
                : 'bg-white border border-gray-300 hover:bg-gray-100'
            }`}
          >
            <div className="font-medium">Monochrome(s)</div>
            <div className="text-xs text-gray-500 mt-1">Art numérique / Œuvre générative</div>
          </button>
          
          {additionalExamples.map((example, index) => (
            <button
              key={index}
              onClick={() => loadExample(index + 1)}
              className={`p-3 text-left rounded ${
                selectedExample === index + 1
                  ? 'bg-blue-100 border border-blue-300' 
                  : 'bg-white border border-gray-300 hover:bg-gray-100'
              }`}
            >
              <div className="font-medium">{example.name}</div>
              <div className="text-xs text-gray-500 mt-1">
                {example.description.substring(0, 60)}...
              </div>
            </button>
          ))}
        </div>
        
        <div className="mt-4 text-sm text-gray-600">
          <p className="font-medium">Instructions :</p>
          <ol className="list-decimal pl-5 mt-1 space-y-1">
            <li>Sélectionnez un exemple ou saisissez votre propre description</li>
            <li>Choisissez votre méthode de génération de tags (Manuel, Basique, IA)</li>
            <li>Dans le mode IA, configurez vos paramètres d'API si vous souhaitez utiliser OpenAI</li>
            <li>Comparez les résultats des différentes méthodes</li>
          </ol>
        </div>
      </div>
      
      {/* Onglets */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex -mb-px">
          <button
            onClick={() => setActiveTab("details")}
            className={`mr-4 py-2 px-3 font-medium text-sm ${
              activeTab === "details"
                ? "border-b-2 border-blue-500 text-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Édition
          </button>
          <button
            onClick={() => setActiveTab("preview")}
            className={`mr-4 py-2 px-3 font-medium text-sm ${
              activeTab === "preview"
                ? "border-b-2 border-blue-500 text-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Aperçu
          </button>
        </nav>
      </div>
      
      {/* Contenu des onglets */}
      <div className="mt-6">
        {activeTab === "details" ? (
          <ArtworkDetailTab 
            formData={formData} 
            setFormData={setFormData} 
          />
        ) : (
          <ArtworkDetailTab 
            formData={formData} 
            setFormData={setFormData} 
            readOnly={true}
          />
        )}
      </div>
      
      {/* Informations supplémentaires */}
      <div className="mt-12 bg-blue-50 p-4 rounded-md border border-blue-200">
        <h3 className="text-lg font-medium text-blue-800 mb-2">À propos des méthodes d'analyse</h3>
        
        <div className="space-y-4 text-sm text-blue-700">
          <div>
            <h4 className="font-medium">Manuel</h4>
            <p>Vous saisissez manuellement les tags ou choisissez parmi les mots extraits directement de la description.</p>
          </div>
          
          <div>
            <h4 className="font-medium">Basique</h4>
            <p>Utilise un système de règles prédéfinies pour analyser la description et suggérer des tags pertinents. Fonctionne entièrement dans le navigateur sans appel API.</p>
          </div>
          
          <div>
            <h4 className="font-medium">IA</h4>
            <p>Utilise une API de traitement du langage naturel (comme OpenAI) pour analyser la description et générer des tags contextuels pertinents. La version démo utilise un mode simulé qui ne nécessite pas de clé API.</p>
          </div>
          
          <div className="pt-2 border-t border-blue-200">
            <p className="italic">Note: En production, l'analyse par IA peut être configurée pour s'exécuter automatiquement à chaque modification de la description, ou être déclenchée manuellement pour économiser les appels API.</p>
          </div>
        </div>
      </div>
    </div>
  );
}